import { Item } from '@wwe-portal/entities/dashboard.type';
import { createStore, StoreApi } from 'zustand';
import { persist, StorageValue } from 'zustand/middleware';
import { safeSetLocalStorage } from '@wwe-portal/web/libs/storage/safe-local-storage';

export interface PipStoreValue {
  showedBanner: Set<string>;
  markBannerShowed: (banner: Item) => Promise<void>;
  hasPip: (banner: Item) => Promise<boolean>;
}

async function digestMessage(message: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  return await crypto.subtle.digest('SHA-256', data);
}

const hashItem = async (item: Item) => {
  const buffer = await digestMessage(JSON.stringify(item));
  return btoa(String.fromCharCode.apply(null, new Uint8Array(buffer)));
};

const createDashboardPipStore = () =>
  createStore(
    persist<PipStoreValue, [], [], PipStoreValue>(
      (set, get) => {
        return {
          showedBanner: new Set<string>(),
          markBannerShowed: async (item) => {
            const hash = await hashItem(item);
            const nextSet = new Set(get().showedBanner);
            nextSet.add(hash);
            set({
              showedBanner: nextSet,
            });
          },
          hasPip: async (item) => {
            const hash = await hashItem(item);
            return !get().showedBanner.has(hash);
          },
        };
      },
      {
        name: 'dashboardPipStore',
        storage: {
          getItem: (name) => {
            const str = localStorage.getItem(name);
            if (!str) return null;
            const existingValue = JSON.parse(str);
            return {
              ...existingValue,
              state: {
                ...existingValue.state,
                showedBanner: new Set(existingValue.state.showedBanner),
              },
            };
          },
          setItem: (name, newValue: StorageValue<PipStoreValue>) => {
            // functions cannot be JSON encoded. Guard the write so a full quota
            // (commonly filled by the offer-views store) can't crash the page.
            safeSetLocalStorage(name, () =>
              JSON.stringify({
                ...newValue,
                state: {
                  ...newValue.state,
                  showedBanner: Array.from(newValue.state.showedBanner),
                },
              })
            );
          },
          removeItem: (name) => localStorage.removeItem(name),
        },
      }
    )
  );

let _store: StoreApi<PipStoreValue>;

export const getDashboardPipStore = () => {
  if (!_store) {
    _store = createDashboardPipStore();
  }

  return _store;
};
